// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FindByErpsParameters, getProductsByErps } from "@bg/App/Services/FindProductByErpService";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    id: string;
    productNumbers: string[];
}

interface Props {
    apiParameter: FindByErpsParameters;
    product: ProductModel;
    productsInfo: ProductInfo[];
    pricingLoaded?: boolean;
    skipPricingAndInventory: boolean;
    pids: string[];
    apiResult: { successful: boolean; products?: ProductModel[]; error?: any };
}

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginFindProductsByErpsData: HandlerType = props => {
    props.dispatch({
        type: "ShopByLook/erpsIsLoading" as any,
    });
};

export const RequestDataFromProductsApi: HandlerType = async props => {
    props.apiResult = await getProductsByErps({ productNumbers: props.parameter.productNumbers });
};

export const DispatchCompletedFindProductsByErpsData: HandlerType = props => {
    const existingProducts = (props.getState() as any).shopByLook.productsByErps.map(
        (product: ProductModel) => product.productNumber,
    );

    const products = props.apiResult.products?.filter(
        product => existingProducts.indexOf(product.productNumber) === -1,
    );
    props.dispatch({
        type: "ShopByLook/ProductsByErps",
        productsByErps: products,
    } as any);
    let prodsInfo = [] as ProductInfo[];
    const pids: any = [];
    if (props.apiResult.products && props.apiResult.products.length > 0) {
        prodsInfo = props.apiResult.products.map((item: any) => {
            pids.push(item.id);
            const productInfo = createFromProduct(item);
            (productInfo as any).erpNumber = item.productNumber;
            return productInfo;
        });
    }
    if (prodsInfo.length > 0) {
        props.productsInfo = prodsInfo;
        props.pids = pids;
    }
};

export const LoadPricing: HandlerType = async props => {
    if (props.skipPricingAndInventory) {
        return;
    }

    let pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productsInfo,
            onSuccess: realTimePricing => {
                if (realTimePricing) {
                    props.productsInfo = props.productsInfo.map(item => {
                        item.pricing = realTimePricing.realTimePricingResults?.find(
                            o => o.productId === item.productId,
                        );
                        return item;
                    });
                }
                pricingLoaded = true;
            },
            onError: () => {
                pricingLoaded = true;
                props.productsInfo &&
                    props.productsInfo.forEach(item => {
                        item.failedToLoadPricing = true;
                    });
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => pricingLoaded);
    }
};

export const LoadInventory: HandlerType = async props => {
    if (props.skipPricingAndInventory) {
        return;
    }
    const pids = props.pids;
    if (!pids || pids.length === 0) {
        return;
    }

    let loadedInventory = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: pids,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productsInfo) {
                        props.productsInfo &&
                            props.productsInfo.forEach(item => {
                                item.failedToLoadInventory = true;
                            });
                    }
                } else if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        item.inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                            o => o.productId === item.productId,
                        );
                        return item;
                    });
                }

                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const SetProductInfo: HandlerType = props => {
    if (props.productsInfo) {
        let productsInfo = {};
        props.productsInfo.forEach(item => {
            const erpNumber = (item as any).erpNumber;
            productsInfo = {
                [erpNumber]: item,
                ...productsInfo,
            };
        });
        props.dispatch({
            type: "ShopByLook/AddProductsInfo",
            productsInfo,
        } as any);
    }
};

export const PopulateApiParameter: HandlerType = (props: { apiParameter: any; parameter: any }) => {
    props.apiParameter = props.parameter;
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginFindProductsByErpsData,
    RequestDataFromProductsApi,
    DispatchCompletedFindProductsByErpsData,
    LoadPricing,
    LoadInventory,
    SetProductInfo,
];

const findProductsByErps = createHandlerChainRunner(chain, "findProductsByErps");
export default findProductsByErps;
